import { useTheme } from '@/src/context/ThemeContext';
import { useTranslation } from '@/src/i18n';
import { ResetPasswordScreen } from '@/src/presentation/screens/ResetPasswordScreen';
import { supabase } from '@/src/core/config/supabase';
import { logger } from '@/src/core/utils';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';

export default function ResetPasswordContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { token_hash, type } = useLocalSearchParams<{ token_hash?: string; type?: string }>();

  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token_hash || type !== 'recovery') {
        setVerifyError(t('auth.invalidResetLink'));
        setVerifying(false);
        return;
      }

      try {
        const { error } = await supabase.auth.verifyOtp({ token_hash, type: 'recovery' });
        if (error) {
          logger.warn('ResetPassword', 'Token verification failed', { error });
          setVerifyError(t('auth.invalidResetLink'));
        }
      } catch (error) {
        logger.error('ResetPassword', 'Token verification exception', { error });
        setVerifyError(t('auth.invalidResetLink'));
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, []);

  const handleUpdatePassword = async () => {
    if (!newPassword.trim()) {
      platformAlert(t('common.error'), t('alerts.fillAllFields'));
      return;
    }

    if (newPassword !== confirmPassword) {
      platformAlert(t('common.error'), t('alerts.passwordsNotMatch'));
      return;
    }

    if (newPassword.length < 6) {
      platformAlert(t('common.error'), t('alerts.passwordMinLength'));
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) {
        logger.error('ResetPassword', 'Update password failed', { error });
        platformAlert(t('common.error'), error.message);
      } else {
        platformAlert(t('common.success'), t('auth.passwordUpdated'), [
          { text: t('common.ok'), onPress: () => router.replace('/auth/login') },
        ]);
      }
    } catch (error: any) {
      logger.error('ResetPassword', 'Update password exception', { error });
      platformAlert(t('common.error'), error.message || t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResetPasswordScreen
      newPassword={newPassword}
      setNewPassword={setNewPassword}
      confirmPassword={confirmPassword}
      setConfirmPassword={setConfirmPassword}
      loading={loading}
      verifying={verifying}
      verifyError={verifyError}
      handleUpdatePassword={handleUpdatePassword}
      navigateToLogin={() => router.replace('/auth/login')}
      colors={colors}
    />
  );
}
